import React, { useState, useEffect } from 'react';
import { 
  Card, Typography, Table, Button, Space, Modal, Form,
  Input, Switch, Popconfirm, message, Row, Col, Tag, Tooltip,
  Upload, Divider, Alert, Progress, Collapse, Tabs, Badge, Statistic
} from 'antd';
import { 
  UserAddOutlined, EditOutlined, DeleteOutlined, 
  EyeOutlined, UploadOutlined,
  DownloadOutlined, FileExcelOutlined, CheckCircleOutlined,
  CloseCircleOutlined, KeyOutlined, CopyOutlined
} from '@ant-design/icons';
import { usuarioService, buyerService } from '../../services/api';
import SolicitudesAcceso from './SolicitudesAcceso';
import { PASSWORD_MIN } from '../../utils/password';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;

const MisClientes = () => {
  const [loading, setLoading] = useState(false);
  // Conteo de pendientes para el badge de la pestaña. Lo reporta el hijo
  // cuando carga la lista, asi no se pide dos veces lo mismo.
  const [pendientes, setPendientes] = useState(0);
  const [tab, setTab] = useState('clientes');
  const [clientes, setClientes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [cargaMasivaModalVisible, setCargaMasivaModalVisible] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [viewingCliente, setViewingCliente] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  
  // Estados para carga masiva
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  // Blanqueo de clave. `blanqueo` guarda la respuesta del backend con la clave
  // temporal: es lo ÚNICO que la tiene en claro, no queda en la BD ni en ningún
  // log, así que si este modal se cierra sin copiarla hay que blanquear de nuevo.
  const [blanqueando, setBlanqueando] = useState(null);   // id_usuario en curso
  const [blanqueo, setBlanqueo] = useState(null);

  useEffect(() => {
    // `tab` en las deps: al volver de "Solicitudes de acceso" hay que releer la
    // lista, porque el comprador que se acaba de autorizar recien ahora califica
    // para aparecer. Sin esto habia que recargar la pagina a mano para verlo.
    if (tab !== 'clientes') return;
    fetchClientes();
    // `pagination.pageSize` en las deps: la paginación es del SERVIDOR
    // (`per_page`), así que cambiar 10 → 50 desde el `showSizeChanger` sin
    // refetch dejaba la grilla con 10 filas y el pie diciendo 50. Desde la
    // página 3 funcionaba de casualidad, porque antd resetea `current` a 1 y
    // era ESE cambio el que disparaba el fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchText, tab]);

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        rol: 3,
      };
      if (searchText) {
        params.search = searchText;
      }
      const response = await usuarioService.getMisClientes(params);
      setClientes(response.data.usuarios || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
      }));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar clientes');
    } finally {
      // En `finally` y no después del bloque: si el catch tira, el spinner
      // queda girando para siempre.
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCliente(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCliente(record);
    form.setFieldsValue({
      nom_usuario: record.nom_usuario,
      email_usuario: record.email_usuario,
      cuit: record.cuit,
      telefono: record.telefono,
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    setViewingCliente(record);
    setViewModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await usuarioService.delete(id);
      message.success('Cliente eliminado exitosamente');
      fetchClientes();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar cliente');
    }
  };

  // Revocar es el UNICO camino para un comprador que se registro solo: no es de
  // esta cabaña y apagarle `estado_usuario` le apagaria la cuenta GLOBAL,
  // tambien en las otras cabañas donde compra. Esto le saca el acceso ACA.
  const handleRevocar = async (record) => {
    try {
      await buyerService.responderAutorizacion(record.id_autorizacion, 'rechazado');
      message.success('Le revocaste el acceso a tus remates');
      fetchClientes();
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos revocar el acceso');
    }
  };

  // Blanqueo de clave. La cabaña es quien verifica la identidad del comprador
  // (ya lo conoce: lo cargó ella o lo autorizó a ofertar), y le pasa la clave
  // temporal por el canal que ya usan. El sistema lo obliga a cambiarla al
  // entrar, así que la clave que viaja por WhatsApp no queda viva.
  const handleBlanquear = async (record) => {
    setBlanqueando(record.id_usuario);
    try {
      const res = await buyerService.blanquearPassword(record.id_usuario);
      setBlanqueo(res.data);
    } catch (err) {
      // "No pudimos blanquear" mentiría: si el backend ya commiteó y lo que
      // falló fue la respuesta (timeout, 502 del proxy), la clave YA cambió y
      // la anterior no sirve más. El productor leería "no pasó nada", no
      // reintentaría, y el comprador llamaría mañana igual de trabado.
      message.error(
        err.response?.data?.error
        || 'No pudimos confirmar el blanqueo. Si el comprador no puede entrar con su clave anterior, blanqueá de nuevo.',
      );
    } finally {
      setBlanqueando(null);
    }
  };

  const handleToggleEstado = async (record) => {
    try {
      const nuevoEstado = record.estado_usuario === 1 ? 0 : 1;
      await usuarioService.update(record.id_usuario, { estado_usuario: nuevoEstado });
      message.success(`Cliente ${nuevoEstado === 1 ? 'activado' : 'desactivado'}`);
      fetchClientes();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const handleSubmit = async (values) => {
    // Guard contra doble-click: si ya estamos enviando, ignorar.
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingCliente) {
        const dataToUpdate = {
          nom_usuario: values.nom_usuario,
          email_usuario: values.email_usuario,
          cuit: values.cuit,
          telefono: values.telefono || null,
        };
        // Sin `password`: la edición ya no la toca. Cambiársela a otro va por
        // "Blanquear clave", que la marca como temporal y deja auditoría.
        await usuarioService.update(editingCliente.id_usuario, dataToUpdate);
        message.success('Cliente actualizado exitosamente');
      } else {
        await usuarioService.createCliente({
          nom_usuario: values.nom_usuario,
          email_usuario: values.email_usuario,
          cuit: values.cuit,
          telefono: values.telefono || null,
          password: values.password,
        });
        message.success('Cliente creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      fetchClientes();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTableChange = (pag) => {
    setPagination(prev => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  // ========== CARGA MASIVA ==========
  const handleOpenCargaMasiva = () => {
    setUploadFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    setCargaMasivaModalVisible(true);
  };

  const handleDescargarPlantilla = async () => {
    try {
      const response = await usuarioService.descargarPlantillaClientes();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clientes_plantilla.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Plantilla descargada');
    } catch (error) {
      console.error('Error descarga:', error);
      message.error('Error al descargar la plantilla');
    }
  };

  const handleUploadChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      setUploadFile(file);
      setUploadResult(null);
    }
  };

  const handleCargarClientes = async () => {
    if (!uploadFile) {
      message.warning('Seleccione un archivo CSV');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await usuarioService.cargarClientesMasivo(formData, (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      });

      setUploadResult(response.data);
      
      if (response.data.total_exitosos > 0) {
        message.success(`${response.data.total_exitosos} clientes cargados exitosamente`);
        fetchClientes();
      }
      
      if (response.data.total_errores > 0) {
        message.warning(`${response.data.total_errores} registros con errores`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar clientes');
      setUploadResult({
        error: error.response?.data?.error || 'Error desconocido'
      });
    } finally {
      // Mismo motivo que en `fetchClientes`: si el catch tira, el spinner queda
      // girando para siempre. Y la barra vuelve a cero, si no el próximo upload
      // arranca mostrando el progreso del anterior.
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nom_usuario',
      key: 'nom_usuario',
      sorter: (a, b) => a.nom_usuario.localeCompare(b.nom_usuario),
    },
    {
      title: 'Email',
      dataIndex: 'email_usuario',
      key: 'email_usuario',
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      key: 'cuit',
      render: (cuit) => cuit || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      render: (tel) => tel || '-',
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      align: 'center',
      // El switch SÓLO para los que cargó la cabaña. `estado_usuario` es la
      // cuenta GLOBAL del comprador: apagarlo lo deja afuera también en las
      // otras cabañas donde compra. Es la misma regla que ya gatea Editar y
      // Eliminar más abajo, y que el backend documenta en
      // `usuario_routes.py:115-118`. Acá se colaba porque el switch no miraba
      // `origen`. Para el que se registró solo, el único control es revocarle
      // el acceso a ESTOS remates.
      render: (_, record) => (record.origen === 'cabana' ? (
        <Switch
          checked={record.estado_usuario === 1}
          onChange={() => handleToggleEstado(record)}
          checkedChildren="Activo"
          unCheckedChildren="Inactivo"
          size="small"
        />
      ) : (
        <Tooltip title="Su cuenta es global: no es de tu cabaña. Acá sólo podés revocarle el acceso a tus remates.">
          <Tag color={record.estado_usuario === 1 ? 'green' : 'red'}>
            {record.estado_usuario === 1 ? 'Activo' : 'Inactivo'}
          </Tag>
        </Tooltip>
      )),
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 140,
      render: (v) => v === 'registro'
        ? <Tooltip title="Se registró desde el micrositio y vos lo autorizaste. No es un usuario de tu cabaña: puede comprarle a otras.">
            <Tag color="blue">Se registró solo</Tag>
          </Tooltip>
        : <Tag>Cargado por vos</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>

          {/* Va para las DOS vías (cargado por la cabaña y registrado+autorizado):
              el que llama diciendo "no puedo entrar" puede ser cualquiera de los
              dos, y el backend acepta ambos. */}
          <Popconfirm
            title="¿Blanquear la clave?"
            description={
              <span style={{ display: 'inline-block', maxWidth: 300 }}>
                Se genera una clave temporal y la actual deja de servir.
                Se la tenés que pasar vos — asegurate de estar hablando
                con {record.nom_usuario}.
              </span>
            }
            onConfirm={() => handleBlanquear(record)}
            okText="Blanquear"
            cancelText="Cancelar"
          >
            <Tooltip title="Blanquear clave">
              <Button
                type="text"
                icon={<KeyOutlined />}
                loading={blanqueando === record.id_usuario}
              />
            </Tooltip>
          </Popconfirm>

          {/* Editar y Eliminar SOLO para los que cargó la cabaña. El comprador
              que se registró solo no le pertenece a nadie. */}
          {record.origen === 'cabana' ? (
            <>
              <Tooltip title="Editar">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Popconfirm
                title="¿Eliminar este cliente?"
                description="Esta acción no se puede deshacer"
                onConfirm={() => handleDelete(record.id_usuario)}
                okText="Sí, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Eliminar">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          ) : (
            <Popconfirm
              title="¿Revocar el acceso?"
              description="No va a poder ofertar más en tus remates. Su cuenta sigue activa."
              onConfirm={() => handleRevocar(record)}
              okText="Revocar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Revocar acceso a tus remates">
                <Button type="text" danger icon={<CloseCircleOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <UserAddOutlined /> Mis Clientes
          </Title>
          <Typography.Text type="secondary">
            Gestiona los clientes que podrán ver y ofertar en tus eventos
          </Typography.Text>
        </Col>
        <Col>
          <Space style={{ display: tab === 'clientes' ? undefined : 'none' }}>
            <Input.Search
              placeholder="Buscar por nombre o email"
              allowClear
              onSearch={(value) => {
                setSearchText(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              style={{ width: 250 }}
            />
            <Button
              icon={<UploadOutlined />}
              onClick={handleOpenCargaMasiva}
            >
              Carga Masiva
            </Button>
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              onClick={handleCreate}
            >
              Nuevo Cliente
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'clientes',
            label: 'Mis clientes',
            children: (
              <Card>
                <Table
                  scroll={{ x: 'max-content' }}
                  columns={columns}
                  dataSource={clientes}
                  rowKey="id_usuario"
                  loading={loading}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} clientes`,
                  }}
                  onChange={handleTableChange}
                />
              </Card>
            ),
          },
          {
            key: 'solicitudes',
            // El badge es el punto: si no salta a la vista que hay gente
            // esperando, nadie entra a la pestaña y el comprador queda trabado
            // sin poder ofertar.
            label: (
              <Badge count={pendientes} offset={[10, -2]} size="small">
                <span>Solicitudes de acceso</span>
              </Badge>
            ),
            children: (
              <Card>
                <SolicitudesAcceso onCambio={setPendientes} />
              </Card>
            ),
          },
        ]}
      />

      {/* Modal Crear/Editar */}
      <Modal
        title={editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="nom_usuario"
            label="Nombre Completo"
            rules={[{ required: true, message: 'Ingrese el nombre' }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            name="email_usuario"
            label="Email"
            rules={[
              { required: true, message: 'Ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
          >
            <Input placeholder="email@ejemplo.com" />
          </Form.Item>

          <Form.Item
            name="cuit"
            label="CUIT"
            rules={[
              { required: true, message: 'Ingrese el CUIT' },
              { pattern: /^\d{11}$/, message: 'El CUIT debe tener 11 dígitos' }
            ]}
          >
            <Input placeholder="20123456789" maxLength={11} />
          </Form.Item>

          <Form.Item
            name="telefono"
            label="Teléfono"
            rules={[
              { max: 30, message: 'Máximo 30 caracteres' },
              {
                pattern: /^[\d\s+()\-.]*$/,
                message: 'Solo dígitos, espacios y los símbolos + - ( ) .',
              },
            ]}
          >
            <Input placeholder="+54 11 1234-5678 (opcional)" maxLength={30} />
          </Form.Item>

          {/* La contraseña se pide SÓLO al crear. Para cambiársela a alguien
              que ya existe está "Blanquear clave", que además la marca como
              temporal y deja auditoría — si este campo siguiera acá habría dos
              caminos para lo mismo y uno de los dos sin ninguna protección. */}
          {!editingCliente && (
            <Form.Item
              name="password"
              label="Contraseña"
              rules={[
                { required: true, message: 'Ingrese la contraseña' },
                { min: PASSWORD_MIN, message: `Mínimo ${PASSWORD_MIN} caracteres` },
              ]}
            >
              <Input.Password placeholder={`Mínimo ${PASSWORD_MIN} caracteres`} />
            </Form.Item>
          )}

          {!editingCliente && (
            <Form.Item
              name="password_confirm"
              label="Confirmar Contraseña"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirme la contraseña' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Las contraseñas no coinciden'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Repetir contraseña" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingCliente ? 'Actualizar' : 'Crear Cliente'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Ver Detalle */}
      <Modal
        title="Detalle del Cliente"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Cerrar
          </Button>,
          // Mismo gate que la columna de acciones: al comprador que se registró
          // solo no se lo edita desde acá. Sin esto el modal era una segunda
          // puerta a `handleEdit`, sin el control que la grilla sí tiene.
          ...(viewingCliente?.origen === 'cabana' ? [
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                setViewModalVisible(false);
                handleEdit(viewingCliente);
              }}
            >
              Editar
            </Button>,
          ] : []),
        ]}
      >
        {viewingCliente && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={8}><strong>Nombre:</strong></Col>
              <Col span={16}>{viewingCliente.nom_usuario}</Col>
              
              <Col span={8}><strong>Email:</strong></Col>
              <Col span={16}>{viewingCliente.email_usuario}</Col>
              
              <Col span={8}><strong>CUIT:</strong></Col>
              <Col span={16}>{viewingCliente.cuit || '-'}</Col>

              <Col span={8}><strong>Teléfono:</strong></Col>
              <Col span={16}>{viewingCliente.telefono || '-'}</Col>

              <Col span={8}><strong>Estado:</strong></Col>
              <Col span={16}>
                <Tag color={viewingCliente.estado_usuario === 1 ? 'green' : 'red'}>
                  {viewingCliente.estado_usuario === 1 ? 'Activo' : 'Inactivo'}
                </Tag>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Modal Carga Masiva */}
      <Modal
        title={<><UploadOutlined /> Carga Masiva de Clientes</>}
        open={cargaMasivaModalVisible}
        onCancel={() => setCargaMasivaModalVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Instrucciones */}
          <Alert
            message="Instrucciones"
            description={
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Descargue la plantilla CSV</li>
                <li>Complete los datos de sus clientes (no modifique los encabezados)</li>
                <li>Guarde el archivo en formato CSV (separado por punto y coma)</li>
                <li>Suba el archivo completado</li>
              </ol>
            }
            type="info"
            showIcon
          />

          {/* Paso 1: descargar plantilla de usuarios */}
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDescargarPlantilla}
            block
          >
            1. Descargar Plantilla de Clientes
          </Button>

          <Divider>2. Subir archivo completado</Divider>

          {/* Upload */}
          <Dragger
            name="file"
            accept=".csv"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleUploadChange}
            fileList={uploadFile ? [{
              uid: '-1',
              name: uploadFile.name,
              status: 'done',
            }] : []}
          >
            <p className="ant-upload-drag-icon">
              <FileExcelOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            </p>
            <p className="ant-upload-text">
              Haga clic o arrastre el archivo CSV aquí
            </p>
            <p className="ant-upload-hint">
              Solo archivos .csv con formato de la plantilla
            </p>
          </Dragger>

          {/* Progress */}
          {uploading && (
            <Progress percent={uploadProgress} status="active" />
          )}

          {/* Botón cargar */}
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleCargarClientes}
            loading={uploading}
            disabled={!uploadFile}
            block
            size="large"
          >
            Cargar Clientes
          </Button>

          {/* Resultados */}
          {uploadResult && !uploadResult.error && (
            <Card size="small" title="Resultado de la carga">
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Statistic 
                    title="Procesados" 
                    value={uploadResult.total_procesados} 
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic 
                    title="Exitosos" 
                    value={uploadResult.total_exitosos}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic 
                    title="Errores" 
                    value={uploadResult.total_errores}
                    valueStyle={{ color: uploadResult.total_errores > 0 ? '#cf1322' : undefined }}
                    prefix={uploadResult.total_errores > 0 ? <CloseCircleOutlined /> : null}
                  />
                </Col>
              </Row>

              {uploadResult.errores && uploadResult.errores.length > 0 && (
                <Collapse style={{ marginTop: 16 }}>
                  <Panel header={`Ver errores (${uploadResult.errores.length})`} key="1">
                    <Table
                      scroll={{ x: 'max-content' }}
                      size="small"
                      dataSource={uploadResult.errores}
                      rowKey={(r, i) => i}
                      pagination={false}
                      columns={[
                        { title: 'Fila', dataIndex: 'fila', width: 60 },
                        { title: 'Email', dataIndex: 'email' },
                        { title: 'Error', dataIndex: 'error' },
                      ]}
                    />
                  </Panel>
                </Collapse>
              )}
            </Card>
          )}

          {uploadResult && uploadResult.error && (
            <Alert
              message="Error en la carga"
              description={uploadResult.error}
              type="error"
              showIcon
            />
          )}
        </Space>
      </Modal>

      {/* Clave temporal. Se muestra UNA sola vez: el backend guarda el hash, no
          la clave, así que si esto se cierra sin copiarla no hay forma de
          recuperarla — hay que blanquear de nuevo. Por eso el aviso es explícito
          y el único botón dice "Ya se la pasé". */}
      <Modal
        title={<><KeyOutlined /> Clave temporal generada</>}
        open={!!blanqueo}
        onCancel={() => setBlanqueo(null)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setBlanqueo(null)}>
            Ya se la pasé
          </Button>,
        ]}
        width={520}
        destroyOnHidden
      >
        {blanqueo && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="Anotala ahora"
              description="Es la única vez que se muestra. Si cerrás sin copiarla, vas a tener que blanquear de nuevo."
            />

            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <Text
                copyable={{ text: blanqueo.password_temporal, icon: <CopyOutlined /> }}
                style={{ fontSize: 26, fontFamily: 'monospace', letterSpacing: 1 }}
                strong
              >
                {blanqueo.password_temporal}
              </Text>
            </div>

            <div>
              <Text type="secondary">Pasásela a </Text>
              <Text strong>{blanqueo.nom_usuario}</Text>
              <Text type="secondary"> — {blanqueo.email_usuario}</Text>
              {blanqueo.telefono && (
                <>
                  <br />
                  <Text type="secondary">Teléfono: </Text>
                  <Text copyable>{blanqueo.telefono}</Text>
                </>
              )}
            </div>

            <Alert
              type="info"
              showIcon
              message="La clave anterior ya no sirve"
              description="Cuando entre con esta, el sistema le va a pedir que elija una nueva. Esta temporal deja de funcionar en ese momento."
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MisClientes;
